import { useState, useRef, useMemo, useEffect } from 'react';
import { Card, Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { library } from '@fortawesome/fontawesome-svg-core';
import { fal } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ReadMoreDoc from './ReadMoreDoc';
import WatchListButton from './WatchListButton';
import RejectButton from './RejectButton';
import RejectRemoveButton from './RejectRemoveButton';
import WatchListRemoveButton from './WatchListRemoveButton';
import CveVectors from '../CveDocument/CveVectors';
import { cleanFloat, getCveFields } from '../../utils';
import { Link } from '../Link/Link';
import { selectAuthProfiles } from '../../redux/selectors/authSelectors';

import {
  faFacebook,
  faSlack,
  faTwitter,
  faLinkedin,
} from '@fortawesome/free-brands-svg-icons';
import { scoreColor } from '../../utils';
import './DocCard.scss';
import { slackShare, msteamShare, emailShare } from '../../services';
import { useSelector } from 'react-redux';
import {
  selectSlackAvailable,
  selectSlackChannels,
  selectMsteamsAvailable,
  selectMsteamsChannels,
} from '../../redux/selectors/integrationsSelectors';
import { selectAuthToken } from '../../redux/selectors/authSelectors';
import AddToProfile from './AddToProfile';
import { docTypes, mitreDocTypes } from '../../constants/constants';
import '../../components/Filters/Filters.scss'
import WatchlistTags from './WatchlistTags';

library.add(fal);

const DocCard = ({
  doc,
  profilePrimary,
  profileSecondary,
  showAddWatchList,
  showRemove,
  list,
  allTags,
  selectedTags,
  cveFields = [],
  activeGroupKey,
  cyberReliabilityLabels,
  hidePlaceholderImage,
  showPill = false,
  activeTags,
  showCuratorAction = false,
  fetchTags,
  showTags = true,
}) => {
  const mailToLink = useRef();
  const token = useSelector(selectAuthToken);
  const allUserProfiles = useSelector(selectAuthProfiles);
  const slackAvailable = useSelector(selectSlackAvailable);
  const slackChannels = useSelector(selectSlackChannels);
  const msteamAvailable = useSelector(selectMsteamsAvailable);
  const msTeamChannels = useSelector(selectMsteamsChannels);
  const [tagSet, setTagSet] = useState([]);
  const [showImage, setShowImage] = useState(true);
  const [showChannelSel, setShowChannelSel] = useState(false);
  const [showMsChannelSel, setShowMsChannelSel] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState();
  const [mailTo, setMailTo] = useState(null);

  useEffect(() => {
    if (doc?.tags?.length && !tagSet?.length) {
      setTagSet(doc?.tags);
    }
  }, [doc?.tags])

  const getEmailShare = (repositoryId, docId) => {
    emailShare(repositoryId, docId).then((res) => {
      if (res) {
        setMailTo(res);
        mailToLink.current.click();
      }
    });
  };

  const handleSlackShare = (action) => {
    const userChannelSize = slackChannels.length;

    if (userChannelSize === 1) {
      // Send API request
      const channelID = slackChannels[0].channelId;
      slackShare(doc.repositoryId, doc.id, channelID).then((res) => {
        toast.success(
          `Document successfully shared to #${slackChannels[0].name}!`,
          {
            position: 'top-right',
            autoClose: 4000,
          }
        );
      });
    } else if (userChannelSize > 1) {
      // Show slack channel Dropdown
      setShowChannelSel(true);
    }
  };

  const handleTeamsShare = (action) => {
    const userChannelSize = msTeamChannels.length;

    if (userChannelSize === 1) {
      // Send API request
      const channelID = msTeamChannels[0].channelId;
      msteamShare(doc.repositoryId, doc.id, channelID).then((res) => {
        toast.success(
          `Document successfully shared to #${msTeamChannels[0].name}!`,
          {
            position: 'top-right',
            autoClose: 4000,
          }
        );
      });
    } else if (userChannelSize > 1) {
      // Show Ms team channel Dropdown
      setShowMsChannelSel(true);
    }
  };

  const updateTagSet = (action, tags) => {
    let nTagSet = [...tagSet];
    if (action === 'add') {
      nTagSet = [...nTagSet, tags];
    } else {
      nTagSet = nTagSet.filter((t) => tags.indexOf(t) === -1);
    }
    setTagSet(nTagSet);
  };

  const hideImg = (event) => {
    setShowImage(false);
  };

  const handleChannelChange = (event) => {
    setSelectedChannel(event.target.value);
    if (event.target.value !== 0) {
      slackShare(doc.repositoryId, doc.id, event.target.value).then((res) => {
        setShowChannelSel(false);
        setSelectedChannel(0);
        toast.success('Document successfully shared!', {
          position: 'top-right',
          autoClose: 4000,
        });
        // if (res.status === 200 || res.status < 299) {
        //   toast.success('Document successfully shared!', {
        //     position: 'top-right',
        //     autoClose: 3000,
        //   });
        // } else {
        //   toast.error('Opps Something went wrong!!', {
        //     position: 'top-right',
        //     autoClose: 3000,
        //   });
        // }
      });
    }
  };

  const handleMsChannelChange = (event) => {
    setSelectedChannel(event.target.value);

    if (event.target.value !== 0) {
      msteamShare(
        token,
        profilePrimary.repositoryId,
        doc.id,
        event.target.value
      ).then((res) => {
        setShowMsChannelSel(false);
        setSelectedChannel(0);
        toast.success('Document successfully shared!', {
          position: 'top-right',
          autoClose: 4000,
        });
      });
    }
  };

  const renderCveFields = (cve) => {
    const showFields = getCveFields();
    return cveFields.map((field) => {
      if (field.label && showFields.includes(field.fieldName)) {
        return (
          <span key={field.fieldName} className='ctx-field-group'>
            <span className='cve-label'>{field.label}: </span>
            <span className='cve-value'> {cve[field.fieldName]}</span>
          </span>
        );
      }
      return null;
    });
  };

  const credibility = (doc?.cyberReliability && cyberReliabilityLabels) ? cyberReliabilityLabels[doc?.cyberReliability] : 'N/A';

  const hasCuratorAccess = useMemo(() => {
    if (!profilePrimary?.repositoryId || !allUserProfiles) return false;

    const repositoryProfiles = Object.values(allUserProfiles).filter(
      profile => profile?.repositoryId === profilePrimary.repositoryId
    );

    return repositoryProfiles.some(profile =>
      profile?.roles?.indexOf('curator') > -1
    );
  }, [profilePrimary?.repositoryId, allUserProfiles]);


  return (
    <>
      <Card
        className={`DocCard ${doc.objectType === 'vulnerability' ? 'cve-doc-card' : ''
          }`}
      >
        <Card.Body>
          {showPill && (
            <div className='pill'>
              {mitreDocTypes.indexOf(doc.objectType) > -1 ? (
                <span>
                  Mitre:&nbsp;
                  <span className='mitre-doc-type'>
                    {docTypes[doc.objectType] || doc.objectType}
                  </span>
                </span>
              ) : doc.objectType === 'vulnerability' ? (
                <span className='cve-doc-type'>
                  critical vulnerability report
                </span>
              ) : doc.objectType === 'article' ? (
                <span className='news-doc-type'>news article</span>
              ) : doc.objectType === 'episode' ? (
                <span className='news-doc-type'>podcast</span>
              ) : (
                <span className='doc-type'>{docTypes[doc.objectType] || doc.objectType}</span>
              )}
            </div>
          )}
          {showAddWatchList && (
            <WatchListButton
              docId={doc.id}
              profileId={profilePrimary.profileId}
            />
          )}

          {showAddWatchList && !showRemove && (
            <RejectButton docId={doc.id} profileId={profilePrimary.profileId} />
          )}

          {showRemove &&
            (list === 'rejectlist' ? (
              <RejectRemoveButton
                docId={doc.id}
                profileId={profilePrimary.profileId}
              />
            ) : (
              <WatchListRemoveButton
                docId={doc.id}
                profileId={profilePrimary.profileId}
              />
            ))}

          {(showCuratorAction && hasCuratorAccess) &&
            <AddToProfile profilePrimary={profilePrimary} docId={doc.id} />
          }
          <div className='flex'>
            {doc.imageUrl && showImage ? (
              <Card.Img variant='left' src={doc.imageUrl} onError={hideImg} />
            ) : hidePlaceholderImage ? null : (
              <div className='image-placeholder'>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="100%"
                  height="60px"
                  viewBox="250 250 240 156"
                  preserveAspectRatio="xMidYMid meet"
                >
                  <path
                    fill="#666"
                    opacity="1.000000"
                    stroke="none"
                    d=" M406.000000,408.243744   C357.031372,408.245514 308.562714,408.248383 260.094086,408.246765   C252.939697,408.246521 252.933609,408.236755 252.931351,401.051758   C252.916580,354.082184 252.901978,307.112640 252.894608,260.143066   C252.893463,252.889206 252.910706,252.875229 260.011017,252.873688   C332.130951,252.858109 404.250916,252.846909 476.370850,252.836243   C483.346527,252.835205 483.381805,252.840714 483.383850,259.677063   C483.398224,307.645782 483.339172,355.614685 483.495026,403.582916   C483.509705,408.105469 481.321381,408.286560 477.953156,408.277344   C454.135590,408.212097 430.317749,408.243103 406.000000,408.243744  M454.499817,260.939545   C392.350983,260.926819 330.202148,260.911224 268.053314,260.905304   C261.240021,260.904663 260.952881,261.174164 260.952942,267.861176   C260.953369,302.351196 260.962036,336.841278 261.021423,371.331207   C261.024994,373.401062 260.491150,375.609589 261.778503,377.650452   C264.080719,376.952667 265.296478,375.074951 266.780975,373.593353   C285.881104,354.530212 305.033630,335.517975 323.952484,316.276154   C327.488831,312.679443 329.562958,312.665131 333.118011,316.295471   C349.901550,333.434540 367.032349,350.233002 383.885468,367.304810   C387.060486,370.521057 388.917816,371.261719 392.550964,367.442535   C403.449280,355.986298 414.978180,345.132629 426.071259,333.857147   C428.962280,330.918579 430.784271,330.906708 433.693542,333.886841   C446.027710,346.521240 458.580536,358.942596 471.100800,371.394196   C472.313721,372.600494 473.209656,374.405853 475.302643,374.492157   C475.454376,374.075867 475.659027,373.776154 475.659424,373.476166   C475.708099,337.321167 475.725494,301.166138 475.804901,265.011200   C475.811707,261.914948 474.303131,260.921112 471.494629,260.944855   C466.163391,260.989929 460.831390,260.946381 454.499817,260.939545  z"
                  />
                  <path
                    fill="#666"
                    opacity="1.000000"
                    stroke="none"
                    d=" M388.342834,290.163300   C398.436890,284.160706 409.422241,286.989929 414.192474,296.525330   C418.715332,305.566223 414.710724,316.083649 405.180084,320.194946   C397.896912,323.336731 388.201324,320.439545 383.987549,313.687714   C379.519440,306.528381 380.534882,295.341492 388.342834,290.163300  z"
                  />
                </svg>
              </div>
            )}
            <section className='main-section'>
              {profilePrimary?.docType === 'cve' ? (
                <h4 className='cve-card-title'>
                  <a href={doc.url} target='_blank' rel='noreferrer'>
                    {doc.id}
                    <FontAwesomeIcon
                      icon={['fal', 'external-link-alt']}
                      style={{
                        verticalAlign: '-1px',
                        marginLeft: '8px',
                        fontSize: '.9rem',
                        color: '#f7921a',
                      }}
                    />
                  </a>
                </h4>
              ) : null}
              {profilePrimary?.docType === 'podcasts' && doc.url ? (
                <Card.Title>
                  <a href={doc.url} target='_blank' rel='noreferrer'>
                    {doc.title}
                    <FontAwesomeIcon
                      icon={['fal', 'external-link-alt']}
                      style={{
                        verticalAlign: '-1px',
                        marginLeft: '8px',
                        fontSize: '.9rem',
                        color: '#f7921a',
                      }}
                    />
                  </a>
                </Card.Title>
              ) : doc.url ? (
                <Card.Title data-testid='news-title'>
                  <Link to={doc.url} external minimal>
                    {doc.title}
                  </Link>
                </Card.Title>
              ) : (
                <Card.Title data-testid='news-title'>{doc.title}</Card.Title>
              )}
              <Card.Text className='ctx-card-info'>
                {doc.objectType === 'vulnerability' && doc.cvssBaseScore ? (
                  <span className='ctx-field-group cve-base-score'>
                    <span className='ctx-label'>Base Score: </span>
                    <span className='ctx-value'>
                      <span
                        className={`severity-coloring severity-coloring--${scoreColor(
                          doc.cvssBaseScore,
                          doc.cvssVersion
                        )}`}
                      >
                        {doc.cvssBaseScore}
                      </span>
                    </span>
                  </span>
                ) : null}
                {doc.cvssVector ? (
                  <span className='ctx-field-group cve-base-score'>
                    <span className='cve-label'>Vector: </span>
                    <span className='cve-value'>
                      <CveVectors vectors={doc.cvssVector} />
                    </span>
                  </span>
                ) : null}
                <span className='ctx-field-group'>
                  <span className='ctx-label'>Published On: </span>
                  <span className='ctx-value' data-testid='news-published-date'>
                    {doc.publishDate}
                  </span>
                </span>

                {doc.isKev && renderCveFields(doc)}

                {doc.showTitle ? (
                  <span className='ctx-field-group'>
                    <span className='ctx-label'>Podcast: </span>
                    <span className='ctx-value ctx-author'>
                      {doc.showTitle}
                    </span>
                  </span>
                ) : null}
                {doc.type !== 'cve' &&
                  doc.type !== 'podcast' &&
                  doc.type !== 'mitre' &&
                  doc.site ? (
                  <span className='ctx-field-group'>
                    <span className='ctx-label'>Source: </span>
                    <span
                      className='ctx-value ctx-source'
                      data-testid='news-source'
                    >
                      {doc.site}
                    </span>
                  </span>
                ) : null}
                {(activeGroupKey === 'news' && doc.cyberReliability) ? (
                  <span className='ctx-field-group'>
                    <span className='ctx-label'>Credibility Score: </span>
                    <span className='ctx-value ctx-author'>
                      {credibility?.label || 'N/A'}
                    </span>
                  </span>
                ) : null}
                {(list === 'recommendations' && doc.score) ? (
                  <span className='ctx-field-group'>
                    <span className='ctx-label'>Recommendation Score: </span>
                    <span className='ctx-value ctx-author'>
                      {cleanFloat(doc.score)}
                    </span>
                  </span>
                ) : null}
                {doc.author ? (
                  <span className='ctx-field-group'>
                    <span className='ctx-label'>Author: </span>
                    <span className='ctx-value ctx-author'>
                      {typeof doc.author === 'object'
                        ? doc.author.map(
                          (v, i) =>
                            `${v}${doc.author.length !== i + 1 ? ',' : ''}`
                        )
                        : doc.author}
                    </span>
                  </span>
                ) : null}
              </Card.Text>

              {showTags ? <WatchlistTags
                allTags={allTags}
                docId={doc.id}
                updateTagSet={updateTagSet}
                selectedTags={selectedTags}
                profileId={profilePrimary.profileId}
                documentTags={tagSet}
                showActions={(list === 'watchlist') && profilePrimary.roles.indexOf('curator') > -1}
                fetchTags={fetchTags}
              /> : null}

              <div className='slack-doc-share-wrap'>
                {slackAvailable && (
                  <Button onClick={handleSlackShare}>
                    <FontAwesomeIcon icon={faSlack} />
                  </Button>
                )}
                {msteamAvailable && (
                  <Button onClick={handleTeamsShare}>
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      viewBox='0 0 48 48'
                      width='18px'
                      height='18px'
                    >
                      <path
                        fill='currentColor'
                        d='M41.5 13A3.5 3.5 0 1 0 41.5 20 3.5 3.5 0 1 0 41.5 13zM4 40l23 4V4L4 8V40z'
                      />
                      <path
                        fill='#fff'
                        d='M21 16.27L21 19 17.01 19.18 16.99 31.04 14.01 30.95 14.01 19.29 10 19.45 10 16.94z'
                      />
                      <path
                        fill='currentColor'
                        d='M36 14c0 2.21-1.79 4-4 4-1.2 0-2.27-.53-3-1.36v-5.28c.73-.83 1.8-1.36 3-1.36C34.21 10 36 11.79 36 14zM38 23v11c0 0 1.567 0 3.5 0 1.762 0 3.205-1.306 3.45-3H45v-8H38zM29 20v17c0 0 1.567 0 3.5 0 1.762 0 3.205-1.306 3.45-3H36V20H29z'
                      />
                    </svg>
                  </Button>
                )}
                <Button
                  variant='link'
                  className='p-0'
                  onClick={() => {
                    const baseUrl =
                      process.env.REACT_APP_ENV === 'prod'
                        ? 'https://billwatch.contexture.ai'
                        : 'https://dev1.contexture.ai';
                    const shareUrl = `${baseUrl}/services/social/share/facebook/${doc.repositoryId}/${doc.id}`;
                    window.open(shareUrl, '_blank');
                  }}
                >
                  <FontAwesomeIcon icon={faFacebook} />
                </Button>
                <Button
                  variant='link'
                  className='p-0'
                  onClick={() => {
                    const baseUrl =
                      process.env.REACT_APP_ENV === 'prod'
                        ? 'https://billwatch.contexture.ai'
                        : 'https://dev1.contexture.ai';
                    const shareUrl = `${baseUrl}/services/social/share/twitter/${doc.repositoryId}/${doc.id}`;
                    window.open(shareUrl, '_blank');
                  }}
                >
                  <FontAwesomeIcon icon={faTwitter} />
                </Button>
                <Button
                  variant='link'
                  className='p-0'
                  onClick={() => {
                    const baseUrl =
                      process.env.REACT_APP_ENV === 'prod'
                        ? 'https://billwatch.contexture.ai'
                        : 'https://dev1.contexture.ai';
                    const shareUrl = `${baseUrl}/services/social/share/linkedin/${doc.repositoryId}/${doc.id}`;
                    window.open(shareUrl, '_blank');
                  }}
                >
                  <FontAwesomeIcon icon={faLinkedin} />
                </Button>
                <a
                  href={mailTo}
                  target='_blank'
                  rel='noreferrer'
                  ref={mailToLink}
                  style={{ cursor: 'pointer' }}
                  onClick={(e) => {
                    if (!mailTo) {
                      e.preventDefault();
                      getEmailShare(doc.repositoryId, doc.id);
                    }
                  }}
                >
                  <FontAwesomeIcon icon={['fal', 'envelope']} />
                </a>

                {showChannelSel && (
                  <div className='choose-slack-channel-share'>
                    <select
                      value={selectedChannel}
                      onChange={handleChannelChange}
                      className='slack-channel-dropdown'
                      name='slack-channel-sel'
                    >
                      <option value='0' key='0'>
                        Select Slack Channel
                      </option>
                      {slackChannels.map(({ name, channelId: id }) => (
                        <option value={`${id}`} key={id}>{`${name}`}</option>
                      ))}
                    </select>
                  </div>
                )}

                {showMsChannelSel && (
                  <div className='choose-slack-channel-share'>
                    <select
                      value={selectedChannel}
                      onChange={handleMsChannelChange}
                      className='slack-channel-dropdown'
                      name='slack-channel-sel'
                    >
                      <option value='0' key='0'>
                        Select Ms Team Channel
                      </option>
                      {msTeamChannels.map(({ name, channelId: id }) => (
                        <option value={`${id}`} key={id}>{`${name}`}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            </section>
          </div>
          {profilePrimary && profilePrimary.repositoryId ? (
            <ReadMoreDoc
              doc={doc}
              profilePrimary={profilePrimary}
              profileSecondary={profileSecondary}
              list={list}
              allTags={allTags}
              className='read-more-container'
              activeTags={activeTags || tagSet}
              updateTagSet={updateTagSet}
            />
          ) : null}
        </Card.Body>
      </Card>
    </>
  );
};
export default DocCard;
